import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

import { Feature } from 'src/app/features/feature-management/models';
import { FeatureManagementService } from 'src/app/features/feature-management/services';

import {
  AvTelemetrySink,
  AvTelemetrySource,
} from '../models/av-telemetry-source';
import {
  AV_TELEMETRY_POLL_INTERVAL_MS,
  AV_TELEMETRY_SCHEMA_VERSION,
  AvTelemetryEnvelope,
  AvTelemetryRecordType,
} from '../models/av-telemetry.model';

/**
 * Vendor-agnostic AV telemetry sampler.
 *
 * Owns everything that is not vendor-specific: the feature gate, spine stamping,
 * per-session sequencing, the in-memory buffer, and the sampling timer for `poll`
 * providers. Vendor SDK knowledge lives entirely in AvTelemetrySource implementations.
 *
 * The buffer here is deliberately in-memory only. Durable buffering (IndexedDB) and
 * upload are separate stories; until those land, `drain()` is the handoff point and the
 * buffer is capped so a long session cannot grow without bound.
 */
@Injectable({ providedIn: 'root' })
export class AvTelemetrySamplerService implements AvTelemetrySink {
  /** ~80 minutes of 1 Hz ticks. Beyond this, ticks are evicted before events. */
  static readonly MAX_BUFFERED_RECORDS = 5_000;

  /**
   * Resolved once per page load via `isFeatureEnabled$`.
   *
   * Not the synchronous `getIsFeatureEnabledWithCaching`: it returns undefined on a
   * cache miss, so a kill switch built on it is unreliably off on first load. It is
   * deprecated for exactly that reason (bug 296592).
   *
   * Not the per-flag `getIsFeatureFlagEnabled` either — that issues its own HTTP GET
   * per call. `isFeatureEnabled$` reads the flag map the app has usually already
   * fetched, and shares one request across concurrent cold callers. Both resolve to
   * false when the fetch fails, so the gate fails closed either way.
   */
  readonly isEnabled$: Observable<boolean>;

  private enabled = false;
  private source: AvTelemetrySource | null = null;
  private records: AvTelemetryEnvelope[] = [];
  private seq = 0;
  private droppedRecords = 0;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly featureManagementService: FeatureManagementService) {
    this.isEnabled$ = this.featureManagementService
      .isFeatureEnabled$(Feature.AvTelemetry)
      .pipe(
        tap((isEnabled: boolean) => (this.enabled = isEnabled)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
  }

  /** Resolved flag value. False until the flag resolves, so callers fail closed. */
  get isEnabled(): boolean {
    return this.enabled;
  }

  get isSampling(): boolean {
    return this.source !== null;
  }

  get bufferedRecordCount(): number {
    return this.records.length;
  }

  /** Records evicted due to the buffer cap. Surfaced so the uploader can report loss. */
  get droppedRecordCount(): number {
    return this.droppedRecords;
  }

  /**
   * Begin sampling from a vendor source. No-op when the flag is off or already sampling.
   * Emits the session's `meta` record, then wires the source.
   */
  start(source: AvTelemetrySource): void {
    if (!this.enabled || this.source) {
      return;
    }

    this.source = source;
    this.seq = 0;
    this.droppedRecords = 0;
    this.records = [];

    this.append('meta', source.describeClient());
    source.attach(this);

    if (source.cadenceMode === 'poll') {
      this.startPolling(source);
    }
  }

  /** Stop sampling and release the source. Buffered records are retained for drain(). */
  stop(): void {
    this.stopPolling();

    if (this.source) {
      this.source.detach();
      this.source = null;
    }
  }

  /** Hand off buffered records and clear the buffer. */
  drain(): AvTelemetryEnvelope[] {
    const drained = this.records;
    this.records = [];
    return drained;
  }

  /** Read the buffer without clearing it. Intended for tests and diagnostics. */
  peek(): readonly AvTelemetryEnvelope[] {
    return this.records;
  }

  // ── AvTelemetrySink ──

  reportTick(payload: unknown): void {
    this.append('tick', payload);
  }

  reportEvent(payload: unknown): void {
    this.append('event', payload);
  }

  // ── Internals ──

  private startPolling(source: AvTelemetrySource): void {
    if (!source.sampleTick) {
      return;
    }

    this.pollIntervalId = setInterval(() => {
      const sample = source.sampleTick?.();
      if (sample !== null && sample !== undefined) {
        this.reportTick(sample);
      }
    }, AV_TELEMETRY_POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollIntervalId !== null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  private append(type: AvTelemetryRecordType, payload: unknown): void {
    if (!this.enabled || !this.source) {
      return;
    }

    this.evictIfFull();

    // Read before incrementing, so the first record of a session is seq 0.
    const seq = this.seq;
    this.seq++;

    this.records.push({
      type: type,
      schemaVersion: AV_TELEMETRY_SCHEMA_VERSION,
      occurredAt: Date.now(),
      vendor: this.source.vendor,
      vendorVersion: this.source.vendorVersion,
      seq: seq,
      payload: payload,
    });
  }

  /**
   * Evict to stay under the cap, preferring the oldest `tick`.
   *
   * Ticks are dense and highly repetitive; `meta` and `event` records carry the
   * diagnostic signal (join failures, disconnects) and are what an investigation
   * actually reads. Dropping the oldest record outright would discard session-start
   * evidence, which is exactly where join failures live.
   */
  private evictIfFull(): void {
    if (this.records.length < AvTelemetrySamplerService.MAX_BUFFERED_RECORDS) {
      return;
    }

    const oldestTickIndex = this.records.findIndex((record) => record.type === 'tick');
    this.records.splice(oldestTickIndex >= 0 ? oldestTickIndex : 0, 1);
    this.droppedRecords++;
  }
}
